if (!window.upcartOpenAfterAdd) {
  // UpCart opens its drawer when an add starts, before /cart/add answers, so the drawer shows the old cart (often
  // "Your cart is empty") for a moment. Turn that early open off and open the drawer once the cart is refreshed.
  let pendingOpen = null;
  let themeAdds = 0;
  window.upcartThemeAddInFlight = false;
  window.upcartStartThemeAdd = () => {
    themeAdds += 1;
    window.upcartThemeAddInFlight = true;
  };
  window.upcartOpenAfterAdd = () => {
    if (pendingOpen) return pendingOpen;
    pendingOpen = (async () => {
      try {
        if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart();
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
          setTimeout(resolve, 100);
        });
      } catch (e) {}
      try {
        if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
      } catch (e) {}
    })().finally(() => (pendingOpen = null));
    return pendingOpen;
  };

  try {
    if (typeof window.upcartOpenOnAddToCartOverride !== 'boolean') window.upcartOpenOnAddToCartOverride = false;
    if (!('upcartShouldOpenOnAddToCartBackgroundNetworkCall' in window)) {
      // Adds made by other scripts keep following UpCart's own "Open cart drawer on add to cart" setting.
      Object.defineProperty(window, 'upcartShouldOpenOnAddToCartBackgroundNetworkCall', {
        configurable: true,
        get() {
          if (window.upcartThemeAddInFlight) return false;
          const upcartSettings = window.upcartSettings && window.upcartSettings.upcartSettings;
          return Boolean(upcartSettings && upcartSettings.openOnAddToCart);
        },
        set(value) {
          Object.defineProperty(window, 'upcartShouldOpenOnAddToCartBackgroundNetworkCall', {
            configurable: true,
            writable: true,
            value,
          });
        },
      });
    }
  } catch (e) {}

  // UpCart decides whether to open only after it has refreshed the cart, so keep the theme's add marked until then.
  window.upcartEndThemeAdd = () => {
    let done = false;
    const end = () => {
      if (done) return;
      done = true;
      window.removeEventListener('aftersell-upcart:public-events:cart-updated', onUpdated);
      themeAdds = Math.max(0, themeAdds - 1);
      window.upcartThemeAddInFlight = themeAdds > 0;
    };
    const onUpdated = () => setTimeout(end);
    window.addEventListener('aftersell-upcart:public-events:cart-updated', onUpdated);
    setTimeout(end, 10000);
  };

  window.addEventListener('aftersell-upcart:public-events:added-to-cart', () => window.upcartOpenAfterAdd());

  // Product forms with an error message post through the theme, not UpCart's interceptor, so Shopify's error (for
  // example "Only 22 items were added to your cart due to availability.") shows in the form. UpCart's own toast is
  // drawn at the top of the document, off-screen once the page is scrolled, and is not announced.
  try {
    const upcartContinue = window.upcartShouldAddToCartContinue;
    window.upcartShouldAddToCartContinue = (event) => {
      try {
        const target = event && event.target;
        const form = target && (target.form || (target.closest && target.closest('form')));
        const productForm = form && form.closest && form.closest('product-form');
        if (
          productForm &&
          productForm.dataset.hideErrors !== 'true' &&
          productForm.querySelector('.product-form__error-message-wrapper')
        )
          return false;
      } catch (e) {}
      return typeof upcartContinue === 'function' ? upcartContinue(event) : true;
    };
  } catch (e) {}
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (!ProductForm.statusElement) {
          ProductForm.statusElement = document.createElement('p');
          ProductForm.statusElement.className = 'visually-hidden';
          ProductForm.statusElement.setAttribute('role', 'status');
          document.body.appendChild(ProductForm.statusElement);
          window.addEventListener('aftersell-upcart:public-events:added-to-cart', () =>
            this.announce(this.dataset.addedText)
          );
        }
      }

      announce(text = '') {
        clearTimeout(ProductForm.announceTimer);
        ProductForm.statusElement.textContent = '';
        if (text) ProductForm.announceTimer = setTimeout(() => (ProductForm.statusElement.textContent = text), 100);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitter = evt.submitter;
        let added = false;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.announce(this.dataset.loadingText);
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const upcartActive = typeof window.upcartOpenCart === 'function';
        if (upcartActive) window.upcartStartThemeAdd();

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              if (upcartActive) {
                try {
                  if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart().catch(() => {});
                } catch (e) {}
              }
              if (submitter && !this.contains(submitter)) this.revealErrorMessage();

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (upcartActive) {
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
              this.error = false;
              const quickAddModal = this.closest('quick-add-modal');
              if (quickAddModal) quickAddModal.hide(true);
              added = true;
              window.upcartOpenAfterAdd();
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id') });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (upcartActive) window.upcartEndThemeAdd();
            this.announce(added ? this.dataset.addedText : '');
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      revealErrorMessage() {
        const wrapper = this.querySelector('.product-form__error-message-wrapper');
        if (!wrapper || wrapper.hidden) return;
        const rect = wrapper.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) wrapper.scrollIntoView({ block: 'center' });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
